"use client";

import {
  useCallback,
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type DetailedHTMLProps,
  type PropsWithChildren,
} from "react";
import type { EmblaCarouselType } from "embla-carousel";
import Icon from "../../atoms/Icon";
import { ICONS } from "../../variables/icons";
import styles from "./EmblaCarouselArrowButtons.module.scss";

type UsePrevNextButtonsReturn = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
): UsePrevNextButtonsReturn => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setPrevBtnDisabled(!api.canScrollPrev());
    setNextBtnDisabled(!api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    // Sync prev/next button disabled state to the embla carousel's external
    // selection state and subscribe to its events.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type ArrowButtonProps = PropsWithChildren<
  DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > & {
    icon?: string;
    size?: number;
  }
>;

export const PrevButton = ({
  children,
  className,
  ...rest
}: ArrowButtonProps) => {
  return (
    <button
      aria-label="Previous slide"
      className={`${styles.arrowButton} -translate-y-1/2 flex items-center justify-center appearance-none no-underline touch-manipulation rounded-[50%] w-10 h-10 absolute top-[50%] cursor-pointer border-0 p-0 m-0 left-[10px] ${className ?? ""}`}
      type="button"
      {...rest}
    >
      <Icon icon={ICONS.CHEVRON_LEFT} />
      {children}
    </button>
  );
};

export const NextButton = ({
  children,
  className,
  ...rest
}: ArrowButtonProps) => {
  return (
    <button
      aria-label="Next slide"
      className={`${styles.arrowButton} -translate-y-1/2 flex items-center justify-center appearance-none no-underline touch-manipulation rounded-[50%] w-10 h-10 absolute top-[50%] cursor-pointer border-0 p-0 m-0 right-[10px] ${className ?? ""}`}
      type="button"
      {...rest}
    >
      <Icon icon={ICONS.CHEVRON_RIGHT} />
      {children}
    </button>
  );
};
